import {useLivePreview} from 'Application/useCases/useLivePreview';

import {getLinkData} from 'Application/utils/linkFieldUtils';

import type {Header} from 'types/payload-types';

/**
 * A custom hook that provides reactive header data with live preview support and logo extraction.
 * This hook connects to the live preview system using `useLivePreview`, enabling the header to reflect changes in real time
 * without requiring a page reload. It listens for updates using the key `"Header"` and supports nested data updates via the `depth` parameter.
 * In addition, the hook extracts the tenant's logo—if available—so it can be conveniently accessed and rendered without redundant checks in the UI.
 * It is particularly useful in CMS-driven layouts where editors may want to preview changes to navigation, branding, or tenant-specific content instantly.
 *
 * @param headerData The initial header data object, typically fetched statically or from server-side props.
 * @returns An object containing the reactive `header` content and an optional `logo` if defined and populated.
 *
 * @example
 * ```tsx
 * const { header, logo } = useHeader(initialHeaderData);
 * return <HeaderComponent data={header} logo={logo} />;
 * ```
 */
export const useHeader = (headerData: Header) => {
    const header = useLivePreview<Header>({
        dataKey: 'Header',
        depth: 2,
        initialData: headerData
    });
    const ctaData = getLinkData(header.cta.button);

    return {
        ctaData,
        navigation: header.navigation ?? []
    };
};